import React, { useState, useContext, useEffect } from 'react';
import { useAsyncFn } from 'react-use';
import {
  Text,
  Button,
  Radio,
  Crate,
  Notification,
  Alert,
  FlexCrate,
} from '@perch-labs/shrike';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAccountContext } from 'contexts/account';
import { copyFor, routes } from 'config';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { Page } from 'styles';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { trackClickEvent } from 'domain/tracking';
import { BackLink } from 'components/back-link';
import { AddPaymentMethodModal } from '../../components/add-payment-method-modal';
import {
  getPaymentCopy,
  getPaymentMethodError,
} from '../../payment-methods.utils';
import {
  GET_ACCOUNT_PAYMENT_METHODS,
  SET_PAYMENT_METHOD_ON_PLANS,
  GET_PLANS_FOR_ADD_MODAL,
} from '../../payment-methods.api';
import { GET_PLAN_PAYMENT_METHOD } from './payment-method-update.api';

const getCopy = copyFor('settings.paymentInformation.updatePlanMethod');
const { paymentSettings } = routes();

export const PaymentMethodUpdate = () => {
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >();
  const [showAddModal, setShowAddModal] = useState(false);
  const { planId } = useParams<{ planId: string }>();
  const { addNotification } = useContext(Notification.Context);
  const { selectedAccountId } = useAccountContext();
  const {
    data: planData,
    error: planError,
    loading: planLoading,
  } = useQuery<GetPlanPaymentMethodQuery, GetPlanPaymentMethodQueryVariables>(
    GET_PLAN_PAYMENT_METHOD,
    {
      variables: { planId: parseInt(planId) },
    }
  );
  const { data, error, loading } = useQuery<
    GetAccountPaymentMethodsQuery,
    GetAccountPaymentMethodsQueryVariables
  >(GET_ACCOUNT_PAYMENT_METHODS, {
    variables: { accountId: selectedAccountId },
  });
  const addModalPlans = useQuery<
    GetPlansForAddModalQuery,
    GetPlansForAddModalQueryVariables
  >(GET_PLANS_FOR_ADD_MODAL, {
    variables: { accountId: selectedAccountId },
  });

  const [mutate] = useMutation<
    SetPaymentMethodOnPlansMutation,
    SetPaymentMethodOnPlansMutationVariables
  >(SET_PAYMENT_METHOD_ON_PLANS);

  const [{ error: mutationError, loading: mutationLoading }, setPaymentMethod] =
    useAsyncFn(
      async options => {
        await mutate(options);
        addNotification({
          displayTime: NOTIFICATION_DISPLAY_TIME,
          message: getCopy('notification'),
          variant: 'success',
        });
      },
      [mutate]
    );

  useEffect(() => {
    if (planData && !selectedPaymentMethodId)
      setSelectedPaymentMethodId(planData.plan.paymentMethodId);
  }, [selectedPaymentMethodId, planData]);

  if (loading || planLoading || addModalPlans.loading)
    return <LoadingPage dataTestId="method-update-loading" />;
  if (
    error ||
    planError ||
    !data ||
    !planData ||
    addModalPlans.error ||
    !addModalPlans.data
  )
    return <ErrorPage />;

  const { paymentMethods } = data.account;
  const { displayName, paymentMethodId } = planData.plan;
  const disableSaveButton = selectedPaymentMethodId === paymentMethodId;
  const setNewPaymentMethod = () =>
    setPaymentMethod({
      variables: {
        accountId: selectedAccountId,
        input: {
          planIds: [parseInt(planId)],
          paymentMethodId: selectedPaymentMethodId,
        },
        skipDisplayName: true,
      },
    });

  const { plans, count } = addModalPlans.data.search.plans;

  return (
    <>
      <BackLink to={paymentSettings} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '8px' }}>
          {getCopy('title')}
        </Text>
        <Text
          textStyle="paragraph500"
          color="secondary"
          margin={{ bottom: '40px' }}
        >
          {getCopy('subTitle', { planDisplayName: displayName })}
        </Text>
        <Crate
          as="section"
          width="100%"
          margin={{ bottom: '40px' }}
          border={{ width: '1px', color: 'tertiary' }}
          borderRadius="8px"
          maxHeight="300px"
          overflow="auto"
        >
          {paymentMethods.map((paymentMethod, index) => {
            const { id } = paymentMethod;
            const { label, text } = getPaymentCopy({
              paymentMethod,
              labelProps: { tag: 'h2' },
            });
            const paymentMethodError = getPaymentMethodError({
              paymentMethod,
              version: 'short',
            });
            return (
              <Crate
                padding="24px 16px"
                key={id}
                border={{
                  top:
                    index !== 0
                      ? { width: '1px', color: 'tertiary' }
                      : undefined,
                }}
              >
                <Radio
                  name="paymentMethods"
                  checked={id === selectedPaymentMethodId}
                  onChange={() => setSelectedPaymentMethodId(id)}
                  value={id.toString()}
                >
                  <Crate margin={{ left: '8px', top: '-2px' }}>
                    {label}
                    <FlexCrate alignItems="center">
                      {text}
                      {!!paymentMethodError && (
                        <Alert margin={{ left: '16px' }}>
                          <Text textStyle="heading400" color="error">
                            {paymentMethodError}
                          </Text>
                        </Alert>
                      )}
                    </FlexCrate>
                  </Crate>
                </Radio>
              </Crate>
            );
          })}
        </Crate>
        {mutationError && (
          <Text margin={{ top: '20px' }}>
            {copyFor('errors.server')('generic')}
          </Text>
        )}
        <Button
          fullWidth
          loading={mutationLoading}
          margin={{ bottom: '20px' }}
          disabled={disableSaveButton}
          onClick={() => {
            setNewPaymentMethod();
            trackClickEvent(
              'paymentInformation.changePaymentMethod.saveChanges'
            );
          }}
        >
          {getCopy('buttons.save')}
        </Button>
        <Button
          backgroundless
          color="primary"
          fullWidth
          onClick={() => {
            setShowAddModal(true);
            trackClickEvent(
              'paymentInformation.changePaymentMethod.addPaymentMethod'
            );
          }}
        >
          {getCopy('buttons.addNewMethod')}
        </Button>
      </Page>
      {showAddModal && (
        <AddPaymentMethodModal
          close={() => {
            setSelectedPaymentMethodId(paymentMethodId);
            setShowAddModal(false);
          }}
          existingPaymentMethodIds={paymentMethods.map(method => method.id)}
          activePlans={plans}
          activePlanCount={count}
        />
      )}
    </>
  );
};
